import { formatDate } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BaseComponent } from '@app/finxone-web-frontend/app/Modules/shared/widgets/base/base.component';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import {
  ClearNotificationsCache,
  FetchNotifications,
  SetNotificationLoadingStatus,
  SetSelectedNotification,
  UpdateNotificationStatus,
} from '@app/finxone-web-frontend/app/lib/actions/notification.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';
import { NotificationModel } from '@app/finxone-web-frontend/app/lib/services/account-service/account.type';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { DownloadDocumentService } from '@app/finxone-web-frontend/app/lib/services/download-document/download-document.service';
import { NotificationService } from '@app/finxone-web-frontend/app/lib/services/notification-service/notification-service.service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { NotificationState } from '@app/finxone-web-frontend/app/lib/state/notifications.state';
import { NotificationCenterActionsMap } from '@app/finxone-web-frontend/app/lib/utils/notification-center-actions/notification-center-action.utils';
import { scrollLoading } from '@app/finxone-web-frontend/app/lib/utils/scrolling-utils/scrolling.utils';
import {
  GenericHandleBarModel,
  buildHandleBarModel,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import {
  isValidCSS,
  transformCSSWithDynamicSelector,
} from '@app/finxone-web-frontend/app/lib/utils/themeing.utils';
import { NotificationsFilters } from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { IconCacheService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  AppBuilderPreviewState,
  BaseCssProperties,
  ListActionValue,
  NavigationOptionsType,
  NotificationMark,
  NotificationMarkChannelEnum,
  NotificationTypeEnum,
  NotificationsStatusRequest,
  ProviderClass,
  SKELETON_LAYOUT_CONFIG_PATH,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
  resolveSkeletonLoadingTemplate,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Observable, Subject, combineLatest, map, of, switchMap, take, takeUntil, tap } from 'rxjs';

@Component({
  standalone: false,
  selector: 'finxone-platform-notification-list',
  templateUrl: './notification-list.component.html',
})
export class NotificationListComponent extends BaseComponent implements OnInit, AfterViewInit {
  @AutoDestroy private destroy$: Subject<void> = new Subject<void>();
  private flutterWebView = (window as any).flutter_inappwebview;
  public content = '';
  private noRecordsTemplate = '';
  public baseHandleBarModel: GenericHandleBarModel;
  private noRecordsTemplateFunction: HandlebarsTemplateDelegate = () => '';
  private fetchingTemplateFunction: HandlebarsTemplateDelegate = () => '';
  public notificationList: NotificationModel[];
  public pageNumber = 1;
  public totalPages = 1;
  public containerHeight: string;
  public selectedOption: { label: string; value: string };
  public filterOptions: { name: string; value: NotificationsFilters; isActive: boolean }[] = [];
  public filtersEnabled = false;
  public filterMarginStyle: baseCssConfigStyle = {
    'margin-top': '0px',
    'margin-bottom': '0px',
  };
  public hasCustomStyles: boolean;
  public notificationLoading = false;
  public fetchingTemplate = '';
  public appBuilderShowFetchingContent = false;
  public appBuilderShowNoRecordsContent = false;
  private hasMore = false;
  @Select(NotificationState.getLoadingStatus)
  private loading$: Observable<boolean>;

  private actionHandlers: Record<string, (actionEvent: any, notification: NotificationModel) => void> = {};

  constructor(
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private notificationService: NotificationService,
    private configService: ConfigService,
    private router: Router,
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private accountService: AccountService,
    private iconCacheService: IconCacheService,
    private el: ElementRef,
    public pageConfig: PageConfigurationProvider,
    private downloadDocumentService: DownloadDocumentService,
  ) {
    super();
    this.registerActionHandlers();
  }

  ngOnInit(): void {
    this.store.dispatch(new ClearNotificationsCache());
    combineLatest([this.notificationService.getNotificationState(), this.loading$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([notificationState, loading]) => {
        this.notificationList = notificationState.notificationList;
        this.hasMore = notificationState?.hasMore ?? false;
        this.evaluateListContainerElementHeight(); // evaluate container height after getting notifs because when we are on the notifications page and reload the page, the container height is not calculated correctly
        this.notificationLoading = loading;
      });

    if (this.dataIn) {
      this.attri = this.dataIn.attri ?? null;
      this.onLoadWidgetConfiguration();
      this.initHandleBars();
      this.applyCustomStyles();
      this.setAppBuilderPreviewState();
    }

    this.store.dispatch(new FetchNotifications(this.pageNumber, this.pageSize));
    this.setupFilterPills();

    this.store.dispatch(
      new UpdateNotificationStatus({
        markAs: NotificationMark.SEEN,
        channel: NotificationMarkChannelEnum.PUSH,
      }),
    );

    if (this.flutterWebView) {
      this.flutterWebView.callHandler('clearNotificationBadgeCountHandler', []);
    }
  }

  private setupFilterPills() {
    this.filtersEnabled = this.attri['filterPillConfig']?.['isEnabled'] ?? false;
    this.filterOptions =
      this.attri['filterPillConfig']?.['filterOptions']?.map((option: { name: string; value: string }) => ({
        name: option.name,
        value: option.value,
        isActive: false,
      })) ?? [];
    const allFilterOption = this.filterOptions.find((option) => option.value === 'all');
    if (this.filtersEnabled && allFilterOption) {
      allFilterOption.isActive = true;
    }
  }

  ngAfterViewInit() {
    this.evaluateListContainerElementHeight();
  }

  private evaluateListContainerElementHeight() {
    this.containerHeight = scrollLoading('notification-list', this.el);
  }

  private initHandleBars() {
    this.content = this.attri?.widgetProperties?.textContent ?? '';
    this.noRecordsTemplate = this.attri?.['noRecordsTemplate'] ?? '';
    this.fetchingTemplate = this.attri?.['fetchingTemplate'] ?? '';
    this.noRecordsTemplateFunction = Handlebars.compile(this.noRecordsTemplate);
    this.fetchingTemplateFunction = Handlebars.compile(this.fetchingTemplate);
    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandleBarModel = model;
      });
  }

  private onLoadWidgetConfiguration(): void {
    this.contentStyle = this.configService.getSpecificBaseCssStyle(this.attri, [
      BaseCssProperties.MARGIN,
      BaseCssProperties.PADDING,
      BaseCssProperties.BACKGROUND_COLOR,
      BaseCssProperties.BOX_SHADOW,
    ]);
  }

  public onNotificationClick(notification: NotificationModel): void {
    this.notificationService
      .markNotificationAsRead(notification.novuId)
      .pipe(
        take(1),
        switchMap(() => {
          const notificationType = notification.payload['notificationType'] as NotificationTypeEnum;
          if (notificationType) {
            return of(notification);
          } else {
            const payload = {
              feedIdentifierIds: [notification.txnId],
            };
            return this.notificationService.fetchNotificationPayloadData(payload).pipe(
              take(1),
              map((data) => {
                notification.payload = {
                  ...data[0].payload,
                  ...notification.payload,
                };
                return notification;
              }),
            );
          }
        }),
      )
      .subscribe((notification) => {
        let destinationURL;
        const notificationType = notification.payload['notificationType'] as NotificationTypeEnum;
        const notificationAction = NotificationCenterActionsMap.get(notificationType);
        const resources = {
          status: notification.payload['status'],
          notificationId: notification.novuId,
          store: this.store,
          accountService: this.accountService,
          authenticationId: notification.payload['authenticationId'],
          requestId: notification.payload['requestId'],
          recordId: notification.payload['recordId'] ?? '',
          invitationUrl: notification.payload['invitationUrl'] ?? '',
        };
        if (notificationAction) {
          destinationURL = notificationAction(resources);
        } else {
          // Fallback to default notification detail page if no specific action is defined
          destinationURL = '/zones/profile/notification/' + resources.notificationId;
        }
        if (destinationURL) {
          this.router.navigateByUrl(destinationURL);
        }
      });
  }

  private handleMarkAsRead(notification: NotificationModel): void {
    if (!notification?.novuId) {
      console.warn('Cannot mark notification as read, missing novuId', notification);
      return;
    }
    this.notificationService
      .markNotificationAsRead(notification.novuId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          notification.read = true;
          this.store.dispatch(
            new UpdateNotificationStatus({
              markAs: 'read',
              channel: 'push',
            } as NotificationsStatusRequest),
          );
        },
        error: (err) => {
          console.error('Failed to mark notification as read', err);
        },
      });
  }

  private handleDownload(notification: NotificationModel): void {
    const payload = {
      feedIdentifierIds: [notification.txnId],
    };
    this.notificationService
      .fetchNotificationPayloadData(payload)
      .pipe(
        take(1),
        tap((data) => {
          notification.payload = {
            ...data[0].payload,
            ...notification.payload,
          };
          this.store.dispatch(new SetSelectedNotification(notification));
        }),
      )
      .subscribe(() => {
        this.initiateDownloadFlow(notification);
      });
  }

  private initiateDownloadFlow(fallbackNotification: NotificationModel): void {
    try {
      // Resolve selected notification from state (fallback to passed in notification)
      const notificationStateSnapshot = this.store.selectSnapshot(NotificationState.getNotification);
      const selectedNotification: NotificationModel | undefined =
        notificationStateSnapshot?.selectedNotification ?? fallbackNotification;

      if (!selectedNotification) {
        console.warn('No notification available to process download');
        return;
      }

      // Prefer explicit download URL if present
      const explicitUrl =
        selectedNotification?.payload?.['downloadUrl'] ?? selectedNotification?.payload?.['downloadURL'];
      if (explicitUrl) {
        window.open(explicitUrl, '_blank');
        return;
      }

      // Get account id from AccountState snapshot
      const accountState = this.store.selectSnapshot(AccountState.getAccounts) as any;
      const accountId = accountState?.currentAccountId ?? '';

      const documentTemplateType =
        selectedNotification?.payload?.['documentTemplateType'] ??
        selectedNotification?.payload?.['downloadDocumentKey'] ??
        'payment-proof-pdf';

      const downloadFileType = selectedNotification?.payload?.['downloadFileType'] ?? 'pdf';

      // Transaction/payment backed download
      const transactionId = selectedNotification?.payload?.['transactionId'] ?? '';
      const notificationType = selectedNotification?.payload?.['notificationType'] ?? '';

      if (transactionId) {
        this.downloadDocumentService
          .downloadPaymentProof(
            accountId,
            documentTemplateType,
            transactionId,
            notificationType,
            downloadFileType,
            this.flutterWebView,
          )
          .pipe(take(1))
          .subscribe();
        return;
      }

      // Exchange/record backed download
      const exchangeId =
        selectedNotification?.payload?.['exchangeId'] ??
        selectedNotification?.payload?.['recordId'] ??
        selectedNotification?.payload?.['exchange_id'] ??
        '';

      if (exchangeId) {
        this.downloadDocumentService
          .downloadExchangeProof(
            documentTemplateType,
            exchangeId,
            notificationType,
            downloadFileType,
            this.flutterWebView,
          )
          .pipe(take(1))
          .subscribe();
        return;
      }

      // Bank statement or other generic document (if applicable) - fallback
      // If notification payload includes startDate/endDate and indicates bank statement template
      const startDate = selectedNotification?.payload?.['startDate'] ?? null;
      const endDate = selectedNotification?.payload?.['endDate'] ?? null;
      if (startDate && endDate) {
        this.downloadDocumentService
          .downloadBankDocument(
            accountId,
            documentTemplateType,
            startDate,
            endDate,
            downloadFileType,
            this.flutterWebView,
          )
          .pipe(take(1))
          .subscribe();
        return;
      }

      console.warn('Could not determine download flow for notification', selectedNotification);
    } catch (err) {
      console.error('Error while initiating download for notification', err);
    }
  }

  private handleDelete(notification: NotificationModel): void {
    //stub
  }

  public onActionTriggered(actionEvent: any, notification: NotificationModel): void {
    const actionKey = actionEvent?.action;
    // First, handle action via registered handler (scalable)
    if (actionKey && this.actionHandlers[actionKey]) {
      try {
        this.actionHandlers[actionKey](actionEvent, notification);
      } catch (err) {
        console.error('Error executing action handler for', actionKey, err);
      }
    } else if (actionKey) {
      console.warn('No action handler registered for', actionKey);
    }

    // Then handle navigation config if present (preserve existing behavior)
    const navigationOptionConfig =
      actionEvent?.config?.navigationOptionConfig ?? actionEvent?.navigationOptionConfig ?? null;
    if (navigationOptionConfig) {
      const navigationUrl = this.handleListActionNavigation(navigationOptionConfig);
      this.router.navigateByUrl(navigationUrl);
    }
  }

  private handleListActionNavigation(navigationOptionConfig?: NavigationOptionsType): string {
    return this.configService.reRouteIfModal(
      navigationOptionConfig?.zone as APP_ZONES,
      navigationOptionConfig?.pageUrl as string,
    );
  }

  // Register action handlers in one place - easy to extend with new ListActionValue entries
  private registerActionHandlers(): void {
    // MARK AS READ
    this.actionHandlers[ListActionValue.MARK_AS_READ] = (actionEvent, notification) =>
      this.handleMarkAsRead(notification);

    // DOWNLOAD
    this.actionHandlers[ListActionValue.DOWNLOAD] = (actionEvent, notification) =>
      this.handleDownload(notification);

    // DELETE
    this.actionHandlers[ListActionValue.DELETE] = (actionEvent, notification) =>
      this.handleDelete(notification);

    // Add more handlers here as new actions are introduced
  }

  public returnRadiusWithUnit(cssValue: number): string {
    return cssValue.toString() + this.attri.css_config.borderRadiusUnit;
  }

  public buildNotificationModel(
    content: string,
    inAppTitle: string,
    inAppDescription: string,
    createdAt: string,
    index: number,
    isRead: boolean,
  ): any {
    const prevNotification = index > 0 ? this.notificationList?.[index - 1] : null;
    return {
      ...this.baseHandleBarModel,
      notificationContent: content,
      notificationTitle: inAppTitle && inAppTitle != '' ? inAppTitle : content,
      notificationDescription: inAppDescription && inAppDescription != '' ? inAppDescription : content,
      notificationDate: this.getApplicableNotificationDate(createdAt),
      notificationTime: formatDate(createdAt, 'HH:mm', 'en'),
      previousNotification: {
        ...prevNotification,
        notificationDate: this.getApplicableNotificationDate(
          prevNotification?.createdAt ?? new Date().toISOString(),
        ),
      },
      index,
      isRead,
    };
  }

  private getApplicableNotificationDate(createdAt: string): string {
    const isDynamicTimestampNeeded = this.content.includes('dynamicTimestamp');
    return isDynamicTimestampNeeded ? createdAt : formatDate(createdAt, 'dd MMMM yyyy', 'en');
  }

  private setAppBuilderPreviewState(): void {
    if (!this.configService.getActiveConfigType().isWorkflow) {
      return;
    }
    const appBuilderState = this.attri?.widgetProperties?.['appBuilderState'];
    this.appBuilderShowFetchingContent = appBuilderState === AppBuilderPreviewState.FETCHING;
    this.appBuilderShowNoRecordsContent = appBuilderState === AppBuilderPreviewState.NO_RECORDS;
  }

  public get listingNotifications(): NotificationModel[] {
    return this.appBuilderShowFetchingContent || this.appBuilderShowNoRecordsContent
      ? []
      : (this.notificationList ?? []);
  }

  public get listingNotificationLoading(): boolean {
    return this.notificationLoading || this.appBuilderShowFetchingContent;
  }

  public renderNoRecordsTemplate(): string {
    return this.noRecordsTemplateFunction({
      ...this.baseHandleBarModel,
    });
  }

  public renderFetchingTemplateFunction(): string {
    return resolveSkeletonLoadingTemplate(
      this.attri?.[SKELETON_LAYOUT_CONFIG_PATH],
      this.fetchingTemplateFunction({
        ...this.baseHandleBarModel,
      }),
    );
  }

  public get pageSize(): number {
    if (this.dataIn.attri['pageSize'] != null) return this.dataIn.attri['pageSize'];
    else return 10;
  }

  public get pagingEnabled(): boolean {
    if (this.dataIn.attri['pagingEnabled'] != null) return this.dataIn.attri['pagingEnabled'];
    else return true;
  }

  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
    if (scrollPercentage > 80 && !this.store.selectSnapshot(NotificationState.getLoadingStatus)) {
      this.handleChildScroll();
    }
  }

  handleChildScroll() {
    if (this.pagingEnabled && this.hasMore) {
      if (this.pageNumber) {
        this.store.dispatch(new SetNotificationLoadingStatus(true));
        this.pageNumber = this.pageNumber + 1;
        this.store.dispatch(new FetchNotifications(this.pageNumber, this.pageSize));
      }
    }
  }

  /**
   * Handles changes to the notification filter selection.
   *
   * Determines whether the current filter toggle should resolve to a specific
   * status (`read` / `unread`) or fall back to fetching all notifications when
   * a previously active filter is deactivated.
   *
   * @param filter - The filter interaction payload containing the filter name,
   *                 value, and active state.
   */
  public onFilterChange(filter: { name: string; value: string; isActive: boolean }) {
    const shouldFetchAllNotifications =
      !filter.isActive &&
      (filter.value === NotificationsFilters.Read || filter.value === NotificationsFilters.Unread);

    const effectiveValue: NotificationsFilters = shouldFetchAllNotifications
      ? NotificationsFilters.All
      : (filter.value as NotificationsFilters);

    this.filterOptions = this.filterOptions.map((f) => {
      return {
        ...f,
        isActive: f.value === effectiveValue,
      };
    });

    this.store.dispatch(new ClearNotificationsCache());
    this.store.dispatch(new FetchNotifications(1, this.pageSize, effectiveValue));
  }

  private applyCustomStyles() {
    this.hasCustomStyles = isValidCSS(this.attri?.['customStyles'] ?? '');

    // return if custom styles are not valid
    if (!this.hasCustomStyles) return;

    const customStyles = this.attri?.['customStyles'] ?? '';
    const newTextContent = transformCSSWithDynamicSelector(customStyles, '#notification-list');

    // check if existing styles are the same as new one then no need to append new styles tag
    const existingStyleElement = document.getElementById('notificationsListStyle');
    if (existingStyleElement && existingStyleElement.textContent === newTextContent) {
      return;
    }

    // remove existing styles if they are different
    if (existingStyleElement) {
      document.head.removeChild(existingStyleElement);
    }

    // append new styles
    const styleElement = document.createElement('style');
    styleElement.id = 'notificationsListStyle';
    styleElement.textContent = newTextContent;
    document.head.appendChild(styleElement);
  }

  public getWidgetStyles(notification: NotificationModel): baseCssConfigStyle {
    return {
      ...this.contentStyle,
      'border-color': notification.read
        ? this.attri.css_config.borderColor
        : this.attri.css_config.alertColor,
      'font-weight': notification.read ? 'normal' : '600',
      'border-width': this.attri.css_config.borderWidth + 'px',
      'border-style': this.attri.css_config.borderStyle,
      'border-bottom-left-radius': this.returnRadiusWithUnit(
        this.attri.css_config.borderRadiusBottomLeft ?? 0,
      ),
      'border-top-left-radius': this.returnRadiusWithUnit(this.attri.css_config.borderRadiusTopLeft ?? 0),
      'border-bottom-right-radius': this.returnRadiusWithUnit(
        this.attri.css_config.borderRadiusBottomRight ?? 0,
      ),
      'border-top-right-radius': this.returnRadiusWithUnit(this.attri.css_config.borderRadiusTopRight ?? 0),
    };
  }
}
